import { ISpectralDiagnostic } from '@stoplight/spectral-core'
import { DiagnosticSeverity } from '@stoplight/types'
import { invariant } from '../lib/invariant'
import { Run } from './Run'

export type IssueType = 'spectral'
export type IssueSeverity = 'notice' | 'warning' | 'error'

export type SourceRange = {
  start: { line: number; column: number }
  end: { line: number; column: number }
}

export abstract class Issue {
  readonly object = 'issue'
  readonly id: string
  readonly type: IssueType
  readonly detail: string
  readonly severity: IssueSeverity
  readonly filePath: string
  readonly range: SourceRange
  readonly run: Run
  constructor(
    id: string,
    type: IssueType,
    detail: string,
    severity: IssueSeverity,
    filePath: string,
    range: SourceRange,
    run: Run
  ) {
    this.id = id
    this.type = type
    this.detail = detail
    this.severity = severity
    this.filePath = filePath
    this.range = range
    this.run = run
  }

  abstract toJSON(): IssueJSON

  static fromJSON(json: IssueJSON): Issue {
    switch (json.type) {
      case 'spectral':
        return SpectralIssue.fromJSON(json)
      default:
        const _: never = json.type
        invariant(false, `Invalid issue type: '${json.type}'`)
    }
  }
}

export type IssueJSON = SpectralIssueJSON

export type SpectralIssueJSON = ReturnType<SpectralIssue['toJSON']>

export class SpectralIssue extends Issue {
  constructor(
    id: string,
    filePath: string,
    range: SourceRange,
    readonly spectralRulesetPath: string,
    readonly spectralDiagnostic: ISpectralDiagnostic,
    run: Run
  ) {
    let issueSeverity: IssueSeverity
    switch (spectralDiagnostic.severity) {
      case DiagnosticSeverity.Error:
        issueSeverity = 'error'
        break
      case DiagnosticSeverity.Warning:
        issueSeverity = 'warning'
        break
      case DiagnosticSeverity.Information:
        issueSeverity = 'notice'
        break
      case DiagnosticSeverity.Hint:
        issueSeverity = 'notice'
        break
      default:
        issueSeverity = 'notice'
        break
    }
    super(id, 'spectral', spectralDiagnostic.message, issueSeverity, filePath, range, run)
  }

  toJSON() {
    return {
      object: this.object,
      id: this.id,
      type: this.type,
      detail: this.detail,
      severity: this.severity,
      file_path: this.filePath,
      range: this.range,
      spectral_ruleset_path: this.spectralRulesetPath,
      spectral_diagnostic: this.spectralDiagnostic,
      run: this.run.toJSON()
    }
  }

  static fromJSON(json: SpectralIssueJSON) {
    return new SpectralIssue(
      json.id,
      json.file_path,
      json.range,
      json.spectral_ruleset_path,
      json.spectral_diagnostic,
      Run.fromJSON(json.run)
    )
  }
}
